import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  InputAdornment,
} from '@mui/material';
import { Add, Search, MoreVert, Delete, Close, DragIndicator } from '@mui/icons-material';
import EditIcon from '../icons/EditIcon';
import { Estimate } from '../../pages/EstimatesPage';
import { getEstimateList, getEstimate, createEstimate, updateEstimateStatus, importAth } from '../../services/estimateService';
import Tooltip from '@mui/material/Tooltip';
import Backdrop from '@mui/material/Backdrop';
import Divider from '@mui/material/Divider';
import { useAuth } from '../../contexts/AuthContext';

interface EstimatesListProps {
}

const EstimatesList: React.FC<EstimatesListProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [estimates, setEstimates] = useState<Estimate[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const mapHeader = (h:any):Estimate=>{
    const status: 'draft' | 'active' | 'completed' = h.status==='finished' ? 'completed' : 'draft';
    const mapped: Estimate = {
      id: h.id,
      estimateNumber: h.estimate_number || undefined, // estimate_number może być null/undefined
      name: h.name || '',
      objectName: h.object_name || '',
      client: h.client || '',
      createdAt: h.prepared_at || h.created_at || '',
      updatedAt: h.updated_at || h.prepared_at || '',
      status: status,
      kz: parseFloat(h.markup_kz) || 0,
      kp: parseFloat(h.markup_kp) || 0,
      z:  parseFloat(h.markup_z) || 0,
      rg: parseFloat(h.rg)||0,
      vat: parseFloat(h.vat) || 0
    };
    return mapped;
  };

  // Funkcja do pobierania i odświeżania listy kosztorysów
  const loadEstimates = useCallback(async () => {
    setLoading(true);
    getEstimateList()
      .then(data=> {
        console.log('[EstimatesList] Loaded estimates:', data);
        const mapped = data.map(mapHeader);
        console.log('[EstimatesList] Mapped estimates:', mapped);
        setEstimates(mapped);
      })
      .catch(err=> {
        console.error('[EstimatesList] Error loading estimates:', err);
      })
      .finally(()=> setLoading(false));
  },[]);

  // Załaduj kosztorysy przy montowaniu komponentu
  useEffect(() => {
    loadEstimates();
  }, [loadEstimates]);

  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedEstimate, setSelectedEstimate] = useState<Estimate | null>(null);
  const [selectedEstimates, setSelectedEstimates] = useState<number[]>([]);
  const [newEstimate, setNewEstimate] = useState({
    name: '',
    objectName: '',
    client: '',
    kz: 5.0,
    kp: 75.0,
    z: 20.0,
    rg: 45.0,
    vat: 23.0,
  });
  const [selectedRowIdx, setSelectedRowIdx] = useState<number>(-1);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [importForm, setImportForm] = useState({
    name: '',
    objectName: '',
    client: '',
    kz: 5.0,
    kp: 75.0,
    z: 20.0,
    rg: 45.0,
    vat: 23.0,
  });
  const [importFile, setImportFile] = useState<File|null>(null);
  const [importing, setImporting] = useState<boolean>(false);

  // Dostosuj kolumny - widoczność
  const ALL_COLUMNS = ['id', 'date', 'client', 'objectName', 'value', 'status', 'actions'];
  const DEFAULT_VIS: Record<string, boolean> = useMemo(() => ({
    id: true,
    date: true,
    client: true,
    objectName: true,
    value: true,
    status: true,
    actions: true
  }), []);
  const VISIBLE_KEY = useMemo(() => `estimates.columns.visible:${user?.user_id || 'anon'}`, [user?.user_id]);
  const [columnsVisible, setColumnsVisible] = useState<Record<string, boolean>>(() => {
    const defaultVis = {
      id: true,
      date: true,
      client: true,
      objectName: true,
      value: true,
      status: true,
      actions: true
    };
    try {
      const key = `estimates.columns.visible:${user?.user_id || 'anon'}`;
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        return { ...defaultVis, ...parsed };
      }
    } catch {}
    return defaultVis;
  });
  const colV = useCallback((key: string) => columnsVisible[key] !== false, [columnsVisible]);

  // Kolejność kolumn
  const COLUMNS_ORDER_DEFAULT: string[] = ['id', 'date', 'client', 'objectName', 'value', 'status', 'actions'];
  const ORDER_KEY = useMemo(() => `estimates.columns.order:${user?.user_id || 'anon'}`, [user?.user_id]);
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const defaultOrder = ['id', 'date', 'client', 'objectName', 'value', 'status', 'actions'];
    try {
      const key = `estimates.columns.order:${user?.user_id || 'anon'}`;
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        // Upewnij się, że 'id' jest na pierwszej pozycji
        const withoutId = parsed.filter((k: string) => k !== 'id');
        const missing = defaultOrder.filter(k => !parsed.includes(k) && k !== 'id');
        return ['id', ...withoutId, ...missing];
      }
    } catch {}
    return [...defaultOrder];
  });

  // Panel kolumn
  const [colsAnchorEl, setColsAnchorEl] = useState<null | HTMLElement>(null);
  const colsOpen = Boolean(colsAnchorEl);
  const [draftColumnsVisible, setDraftColumnsVisible] = useState<Record<string, boolean> | null>(null);
  const [draftOrder, setDraftOrder] = useState<string[] | null>(null);
  const [draftWidths, setDraftWidths] = useState<Record<string, number>>({});
  const [colResizeMode, setColResizeMode] = useState<boolean>(false);
  const [dragCol, setDragCol] = useState<{ key: string; startX: number; startW: number } | null>(null);

  // Szerokości kolumn
  const defaultColumnWidths = useMemo<Record<string, number>>(() => ({
    id: 80,
    date: 120,
    client: 220,
    objectName: 220,
    value: 90,
    status: 90,
    actions: 60
  }), []);
  const WIDTHS_KEY = useMemo(() => `estimates.columns.widths:${user?.user_id || 'anon'}`, [user?.user_id]);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    const defaults = {
      id: 80,
      date: 120,
      client: 220,
      objectName: 220,
      value: 90,
      status: 90,
      actions: 60
    };
    try {
      const key = `estimates.columns.widths:${user?.user_id || 'anon'}`;
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        return { ...defaults, ...parsed };
      }
    } catch {}
    return defaults;
  });

  const getColWidthPx = useCallback((key: string): string => {
    const def = defaultColumnWidths[key] ?? 40;
    const cur = columnWidths[key];
    const width = typeof cur === 'number' ? Math.max(cur, def) : def;
    return `${width}px`;
  }, [columnWidths, defaultColumnWidths]);

  // Obsługa przeciągania do zmiany szerokości kolumn
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragCol) return;
      e.preventDefault();
      const dx = e.clientX - dragCol.startX;
      const newW = Math.max(24, dragCol.startW + dx);
      setColumnWidths(prev => ({ ...prev, [dragCol.key]: newW }));
    };
    const onUp = (e: MouseEvent) => {
      e.preventDefault();
      setDragCol(null);
      try {
        const payload = JSON.stringify(columnWidths);
        localStorage.setItem(WIDTHS_KEY, payload);
      } catch {}
    };
    if (dragCol) {
      try { document.body.style.cursor = 'col-resize'; } catch {}
      window.addEventListener('mousemove', onMove, { passive: false } as any);
      window.addEventListener('mouseup', onUp as any);
    }
    return () => {
      window.removeEventListener('mousemove', onMove as any);
      window.removeEventListener('mouseup', onUp as any);
      try { document.body.style.cursor = ''; } catch {}
    };
  }, [dragCol, columnWidths, WIDTHS_KEY]);

  // Zapisz szerokości kolumn
  useEffect(() => {
    if (!dragCol) {
      try {
        const payload = JSON.stringify(columnWidths);
        localStorage.setItem(WIDTHS_KEY, payload);
      } catch {}
    }
  }, [columnWidths, WIDTHS_KEY, dragCol]);

  // Synchronizuj colResizeMode z otwarciem okna
  useEffect(() => {
    if (!colsOpen && colResizeMode) {
      setColResizeMode(false);
      setDragCol(null);
    }
  }, [colsOpen, colResizeMode]);

  // ESC zamyka okno "Dostosuj kolumny" i wyłącza tryb resize
  useEffect(() => {
    if (!colsOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setDragCol(null);
        setColResizeMode(false);
        setColsAnchorEl(null);
        setDraftColumnsVisible(null);
        setDraftOrder(null);
        setDraftWidths({});
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [colsOpen]);

  // Zapisz zmiany widoczności kolumn
  useEffect(() => {
    try {
      localStorage.setItem(VISIBLE_KEY, JSON.stringify(columnsVisible));
    } catch {}
  }, [VISIBLE_KEY, columnsVisible]);

  // Zapisz kolejność kolumn
  useEffect(() => {
    try {
      localStorage.setItem(ORDER_KEY, JSON.stringify(columnOrder));
    } catch {}
  }, [ORDER_KEY, columnOrder]);

  // Funkcja renderująca nagłówek kolumny z możliwością zmiany szerokości
  const renderResizableTh = (keyId: string, label: string, align?: 'left' | 'right' | 'center') => (
    <TableCell
      align={align}
      key={keyId}
      sx={{
        whiteSpace: keyId === 'date' || keyId === 'value' || keyId === 'status' || keyId === 'actions' ? 'nowrap' : 'normal',
        wordBreak: keyId === 'client' || keyId === 'objectName' ? 'break-word' : 'normal',
        minWidth: keyId === 'date' ? 120 : keyId === 'client' || keyId === 'objectName' ? 220 : keyId === 'value' || keyId === 'status' ? 90 : keyId === 'actions' ? 60 : 120,
        maxWidth: keyId === 'client' || keyId === 'objectName' ? 220 : 'none',
        width: getColWidthPx(keyId),
        position: 'relative',
        userSelect: 'none'
      }}
    >
      {label}
      {colResizeMode && (
        <Box
          role="separator"
          aria-orientation="vertical"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDragCol({
              key: keyId,
              startX: e.clientX,
              startW: parseInt(getColWidthPx(keyId)) || (columnWidths[keyId] ?? defaultColumnWidths[keyId] ?? 40)
            });
          }}
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            width: '12px',
            height: '100%',
            cursor: 'col-resize',
            zIndex: 6,
            touchAction: 'none',
            backgroundImage: 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' }
          }}
        />
      )}
    </TableCell>
  );

  // Funkcja renderująca colgroup
  const renderColGroup = (order: string[]) => (
    <colgroup>
      {order.map((key) => (
        colV(key) ? <col key={`col-${key}`} style={{ width: getColWidthPx(key) }} /> : null
      ))}
    </colgroup>
  );

  // Filtrowanie po wyszukiwaniu
  const filteredEstimates = estimates.filter(estimate => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (estimate.name || '').toLowerCase().includes(searchLower) ||
      (estimate.objectName || '').toLowerCase().includes(searchLower) ||
      (estimate.client || '').toLowerCase().includes(searchLower) ||
      (estimate.estimateNumber || '').toLowerCase().includes(searchLower);
  });

  // Keyboard navigation and shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey) return;
      if (anchorEl) return; // jeśli otwarte menu akcji, nie obsługuj globalnie klawiszy
      if (openDialog || uploadDialogOpen) return;
      if (document.activeElement && (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA')) {
        if (e.key === 'Escape') {
          (document.activeElement as HTMLElement).blur();
          setTimeout(() => {
            setSelectedRowIdx(0);
            tableContainerRef.current?.focus();
          }, 0);
        }
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedRowIdx(idx => Math.min(idx + 1, filteredEstimates.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedRowIdx(idx => Math.max(idx - 1, 0));
      } else if (e.key === 'Enter') {
        if (selectedRowIdx >= 0 && selectedRowIdx < filteredEstimates.length) {
          handleEstimateClick(filteredEstimates[selectedRowIdx].id);
        }
      } else if (e.key === 's' || e.key === 'S') {
        e.preventDefault();
        searchInputRef.current?.focus();
      } else if (e.key === 'a' || e.key === 'A') {
        if (selectedRowIdx >= 0 && selectedRowIdx < filteredEstimates.length) {
          setAnchorEl(document.getElementById(`actions-${filteredEstimates[selectedRowIdx].id}`));
          setSelectedEstimate(filteredEstimates[selectedRowIdx]);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [filteredEstimates, selectedRowIdx, openDialog, uploadDialogOpen, anchorEl]);

  useEffect(() => {
    if (selectedRowIdx >= 0 && tableContainerRef.current) {
      const row = tableContainerRef.current.querySelectorAll('tr[data-row-idx]')[selectedRowIdx] as HTMLElement;
      row?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedRowIdx]);

  const getStatusColor = (status: Estimate['status']) => {
    if(status==='completed') return 'success';
    return 'default';
  };

  const getStatusLabel = (status: Estimate['status']) => {
    return status==='completed' ? 'Zakończony' : 'Roboczy';
  };

  const handleCreateEstimate = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const payload = {
        name: newEstimate.name,
        object_name: newEstimate.objectName,
        client: newEstimate.client,
        markup_kp: newEstimate.kp,
        markup_kz: newEstimate.kz,
        markup_z: newEstimate.z,
        vat: newEstimate.vat,
        preparer: 'SYSTEM',
        prepared_at: today,
      } as const;

      const created = await createEstimate(payload);

      // Odśwież listę kosztorysów - dodaj nowy kosztorys na początku
      setEstimates(prev => [mapHeader(created), ...prev]);

      setOpenDialog(false);
      
      // Przejdź do widoku detali
      navigate(`/app/estimates/${created.id}`);
      setNewEstimate({
        name: '',
        objectName: '',
        client: '',
        kz: 5.0,
        kp: 15.0,
        z: 8.0,
        rg: 45.0,
        vat: 23.0,
      });
    } catch (err) {
      console.error('Błąd podczas tworzenia kosztorysu:', err);
    }
  };

  // Funkcja do odświeżania listy kosztorysów
  const refreshEstimatesList = async () => {
    try {
      setLoading(true);
      const data = await getEstimateList();
      console.log('Odświeżono listę kosztorysów:', data);
      const mapped = data.map(mapHeader);
      setEstimates(mapped);
    } catch (err) {
      console.error('Błąd odświeżania listy kosztorysów:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, estimate: Estimate) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedEstimate(estimate);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedEstimate(null);
  };

  const handleEditEstimate = () => {
    if (selectedEstimate) {
      navigate(`/app/estimates/${selectedEstimate.id}`);
    }
    handleMenuClose();
  };

  const applyStatus = async (status:'draft'|'finished'|'deleted')=>{
    if(!selectedEstimate) return;
    try{
      await updateEstimateStatus(selectedEstimate.id, status);
      if(status==='deleted'){
        setEstimates(prev=> prev.filter(e=>e.id!==selectedEstimate.id));
      }else{
        setEstimates(prev=> prev.map(e=> e.id===selectedEstimate.id ? {...e, status: status==='draft'?'draft':'completed'} : e));
      }
    }catch(err){console.error(err);}finally{handleMenuClose();}
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = filteredEstimates.map((estimate) => estimate.id);
      setSelectedEstimates(newSelected);
      return;
    }
    setSelectedEstimates([]);
  };

  const handleSelectEstimate = (id: number) => {
    const selectedIndex = selectedEstimates.indexOf(id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedEstimates, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedEstimates.slice(1));
    } else if (selectedIndex === selectedEstimates.length - 1) {
      newSelected = newSelected.concat(selectedEstimates.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedEstimates.slice(0, selectedIndex),
        selectedEstimates.slice(selectedIndex + 1),
      );
    }

    setSelectedEstimates(newSelected);
  };

  const isSelected = (id: number) => selectedEstimates.indexOf(id) !== -1;

  // klik w wiersz tabeli
  const handleEstimateClick = (id: number) => {
    navigate(`/app/estimates/${id}`);
  };

  return (
    <Box sx={{ bgcolor: '#FAFAFA', mt: '20px' }}>
      {/* Nagłówek */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 0, mb: 2 }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '20px',
            lineHeight: '24px',
            fontWeight: 400,
            color: '#020242',
            m: 0,
            mt: '8.5px'
          }}
        >
          Kosztorysy
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Tooltip title="Wczytaj kosztorys">
            <IconButton
              onClick={() => setUploadDialogOpen(true)}
              sx={{ 
                width: 40,
                height: 40,
                borderRadius: '4px',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
              }}
            >
              <Box
                component="img"
                src="/assets/icons/upload_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg"
                alt="Wczytaj kosztorys"
                sx={{ 
                  width: 24, 
                  height: 24
                }}
              />
            </IconButton>
          </Tooltip>
          <Button 
            variant="contained" 
            onClick={() => setOpenDialog(true)}
            data-action="add"
            data-hotkey-add="true"
            sx={{ 
              width: 116,
              height: 40,
              borderRadius: '4px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              minWidth: 'auto',
              backgroundColor: '#4040DB',
              color: '#fff',
              '&:hover': { backgroundColor: '#1F1FB0' },
              '&:active': { backgroundColor: '#1A1A98' }
            }}
          >
            Dodaj kosztorys
          </Button>
        </Box>
      </Box>

      {/* Sekcja filtrów */}
      <Box 
        sx={{ 
          display: 'grid',
          gridTemplateColumns: '300px',
          columnGap: '24px',
          alignItems: 'start',
          height: 56,
          mb: 2,
          bgcolor: '#FAFAFA'
        }}
      >
        <TextField
          size="small"
          placeholder="Szukaj w kosztorysach"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          inputRef={searchInputRef}
          inputProps={{ 
            'data-role': 'page-filter',
            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => { 
              if (e.key === 'Escape') { 
                e.preventDefault(); 
                e.stopPropagation(); 
                try { (e.currentTarget as HTMLInputElement).blur(); } catch {} 
              } 
            } 
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ width: 20, height: 20, color: '#595966' }} />
              </InputAdornment>
            )
          }}
          sx={{ 
            width: 300,
            mt: '20px',
            '& .MuiOutlinedInput-root': {
              height: 36,
              backgroundColor: '#fff',
              borderRadius: '4px'
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#d4d4d9'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#595966'
            },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#595966 !important'
            },
            '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
            '& .MuiInputBase-input': { px: '12px', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' },
            '& .MuiInputBase-input::placeholder': { color: '#B9B9B9', opacity: 1 }
          }}
        />
      </Box>

      {/* Zakładka */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '2px', height: 40, mt: '26.5px', mb: 0 }}>
        <Box
          sx={{
            textTransform: 'none',
            color: '#020242',
            fontFamily: 'Roboto, sans-serif',
            fontSize: '14px',
            fontWeight: 500,
            px: 3,
            height: 40,
            bgcolor: '#E6E6EC',
            borderRadius: '4px 4px 0 0',
            display: 'flex',
            alignItems: 'center',
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0
          }}
        >
          Wszystkie ({filteredEstimates.length})
        </Box>
        <IconButton
          size="small"
          sx={{
            width: 32,
            height: 32,
            borderRadius: '4px',
            mt: '4px',
            p: 0,
            bgcolor: colsOpen ? '#E0E0F5' : 'transparent',
            transition: 'background-color 0.15s ease',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
          }}
          onClick={(e) => {
            setDraftColumnsVisible({ ...columnsVisible });
            setDraftOrder([...columnOrder]);
            setDraftWidths({ ...columnWidths });
            setColsAnchorEl(e.currentTarget);
            setColResizeMode(true);
          }}
        >
          <Box
            component="img"
            alt="kolumny"
            src="/assets/icons/frame-344.svg?v=2"
            sx={{ width: 32, height: 32 }}
          />
        </IconButton>
      </Box>

      {/* Okno "Dostosuj kolumny" */}
      <Backdrop open={colsOpen} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1, backgroundColor: 'rgba(0,0,0,0.35)', pointerEvents: 'none' }}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 0,
            right: 0,
            height: '50vh',
            width: '280px',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0px 5px 30px rgba(0,0,0,0.15)',
            borderRadius: 0,
            zIndex: (theme) => theme.zIndex.modal + 2,
            backgroundColor: '#fff',
            pointerEvents: 'auto'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Box
            sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: '0 0 auto', position: 'sticky', top: 0, bgcolor: '#FFFFFF', zIndex: 1 }}
          >
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 16, color: '#2D2D32' }}>Dostosuj kolumny</Typography>
            <IconButton size="small" onClick={() => { 
              setColsAnchorEl(null); 
              setColResizeMode(false); 
              setDraftColumnsVisible(null); 
              setDraftOrder(null); 
              setDraftWidths({});
              setDragCol(null);
            }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ px: 0, py: 1.5, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
            {(() => {
              const list = draftColumnsVisible ?? columnsVisible;
              const ord = draftOrder ?? columnOrder;
              const columnLabels: Record<string, string> = {
                id: 'ID',
                date: 'Data',
                client: 'Zamawiający',
                objectName: 'Nazwa Obiektu',
                value: 'Wartość',
                status: 'Status',
                actions: 'Akcje'
              };
              return ord.map((key) => (
                <Box key={key} sx={{ py: 1, px: 2 }}
                  draggable
                  onDragStart={(e) => { e.dataTransfer.setData('text/plain', key); }}
                  onDragOver={(e) => { e.preventDefault(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const src = e.dataTransfer.getData('text/plain');
                    if (!src || src === key) return;
                    setDraftOrder(prev => {
                      const cur = [...(prev || ord)];
                      const from = cur.indexOf(src);
                      const to = cur.indexOf(key);
                      if (from < 0 || to < 0) return cur;
                      cur.splice(to, 0, cur.splice(from, 1)[0]);
                      return cur;
                    });
                  }}
                >
                  <Box sx={{ border: '1px solid #d4d4d9', borderRadius: '4px', height: 36, width: 257, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <IconButton onClick={() => setDraftColumnsVisible(prev => ({ ...(prev || {}), [key]: !((prev || list)[key] !== false) }))} size="small" sx={{ p: 0 }} aria-label="toggle column">
                        <Box component="img" src={(draftColumnsVisible ?? columnsVisible)[key] !== false ? '/assets/icons/checkbox-on.svg' : '/assets/icons/checkbox-off.svg'} alt="check" sx={{ width: 23, height: 24 }} />
                      </IconButton>
                      <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32' }}>{columnLabels[key] || key}</Typography>
                    </Box>
                    <DragIndicator fontSize="small" sx={{ color: '#595966', cursor: 'grab' }} />
                  </Box>
                </Box>
              ));
            })()}
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '24px', flex: '0 0 auto', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)', position: 'sticky', bottom: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
            <Button variant="text" onClick={() => { 
              setColsAnchorEl(null); 
              setColResizeMode(false); 
              setDraftColumnsVisible(null); 
              setDraftOrder(null); 
              setDraftWidths({});
              setDragCol(null);
            }} sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#2D2D32', textTransform: 'none' }}>Anuluj</Button>
            <Button variant="contained" sx={{ width: 123, height: 40, borderRadius: '4px', textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: 14, backgroundColor: '#020242', '&:hover': { backgroundColor: '#020242' } }} onClick={() => {
              if (draftColumnsVisible) {
                setColumnsVisible(draftColumnsVisible);
              }
              if (draftOrder) {
                const normalized = draftOrder.filter((k) => ALL_COLUMNS.includes(k)).concat(ALL_COLUMNS.filter(k => !draftOrder.includes(k)));
                setColumnOrder(normalized);
              }
              // Zapisz szerokości kolumn z draftWidths
              const widthsToSave = { ...columnWidths };
              const finalOrder = draftOrder ?? columnOrder;
              finalOrder.forEach((key) => {
                const draftW = draftWidths[key];
                if (draftW && typeof draftW === 'number' && draftW >= 24) {
                  widthsToSave[key] = draftW;
                }
              });
              setColumnWidths(widthsToSave);
              try {
                localStorage.setItem(WIDTHS_KEY, JSON.stringify(widthsToSave));
              } catch {}
              setColsAnchorEl(null);
              setColResizeMode(false);
              setDraftColumnsVisible(null);
              setDraftOrder(null);
              setDraftWidths({});
              setDragCol(null);
            }}>Zapisz zmiany</Button>
          </Box>
        </Paper>
      </Backdrop>

      {/* Tabela w stylu ProjectsPage */}
      <TableContainer 
        ref={tableContainerRef} 
        component={Paper} 
        sx={{ 
          height: 432, 
          overflow: 'auto', 
          position: 'relative', 
          border: '1px solid #d0d0d6', 
          borderBottom: '1px solid #d0d0d6', 
          borderRadius: 0, 
          borderTopRightRadius: 4, 
          borderBottomLeftRadius: 4, 
          borderBottomRightRadius: 4, 
          overflowX: 'hidden', 
          overflowY: 'auto', 
          boxShadow: 'none', 
          scrollbarWidth: 'none', 
          msOverflowStyle: 'none', 
          '&::-webkit-scrollbar': { width: 0, height: 0 } 
        }}
        tabIndex={0}
      >
        <Table 
          size="small" 
          stickyHeader 
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            '& .MuiTableCell-root': { 
              padding: '1px 4px', 
              fontSize: '0.85rem', 
              borderRadius: 0,
              fontFamily: 'Roboto, sans-serif'
            },
            '& .MuiTableCell-head': { 
              position: 'sticky', 
              top: 0, 
              zIndex: 2, 
              backgroundColor: '#FAFAFA', 
              borderRight: '1px solid #d4d4d9', 
              borderRadius: 0, 
              boxShadow: 'none',
              fontFamily: 'Roboto, sans-serif',
              fontWeight: 500,
              color: '#2D2D32'
            },
            '& .MuiTableCell-body': { 
              borderRight: 'none', 
              borderRadius: 0,
              color: '#2D2D32',
              fontFamily: 'Roboto, sans-serif'
            },
            '& .MuiTableRow-head > *:last-child': { 
              borderRight: 'none', 
              borderTopRightRadius: 4 
            },
            '& .MuiTableRow-root:hover': {
              backgroundColor: '#F5F5FF !important'
            }
          }}
        >
          {renderColGroup(columnOrder)}
          <TableHead>
            <TableRow>
              {columnOrder.map((key) => colV(key) ? (
                key === 'id' ? renderResizableTh(key, 'ID') :
                key === 'date' ? renderResizableTh(key, 'Data') :
                key === 'client' ? renderResizableTh(key, 'Zamawiający') :
                key === 'objectName' ? renderResizableTh(key, 'Nazwa Obiektu') :
                key === 'value' ? renderResizableTh(key, 'Wartość', 'right') :
                key === 'status' ? renderResizableTh(key, 'Status') :
                key === 'actions' ? renderResizableTh(key, 'Akcje') :
                null
              ) : null)}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columnOrder.filter(k => colV(k)).length} align="center" sx={{ py: 4 }}>
                  Ładowanie...
                </TableCell>
              </TableRow>
            ) : filteredEstimates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnOrder.filter(k => colV(k)).length} align="center" sx={{ py: 4 }}>
                  {estimates.length === 0 ? 'Brak kosztorysów' : 'Brak wyników wyszukiwania'}
                </TableCell>
              </TableRow>
            ) : (
              filteredEstimates.map((estimate, idx) => {
              const isActive = idx === selectedRowIdx;
              return (
                <TableRow
                  key={estimate.id}
                  data-row-idx={idx}
                  hover
                  selected={isActive}
                  sx={isActive ? { outline: '2px solid #1976d2', outlineOffset: -2, background: '#e3f2fd' } : {}}
                >
                            {columnOrder.map((key) => colV(key) ? (
                              key === 'id' ? <TableCell key={key} sx={{ whiteSpace: 'nowrap', width: getColWidthPx(key) }}>
                                <Link 
                                  to={`/app/estimates/${estimate.id}`}
                                  style={{
                                    textDecoration: 'underline',
                                    fontFamily: 'Roboto, sans-serif',
                                    fontWeight: 400,
                                    fontSize: '14px',
                                    color: 'rgb(63, 63, 220)',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: 'block'
                                  }}
                                >
                                  {estimate.estimateNumber || '-'}
                                </Link>
                              </TableCell> :
                    key === 'date' ? <TableCell key={key} sx={{ whiteSpace: 'nowrap', width: getColWidthPx(key) }}>{estimate.createdAt}</TableCell> :
                    key === 'client' ? <TableCell key={key} sx={{ whiteSpace: 'normal', wordBreak: 'break-word', width: getColWidthPx(key) }}>{estimate.client}</TableCell> :
                    key === 'objectName' ? <TableCell key={key} sx={{ whiteSpace: 'normal', wordBreak: 'break-word', width: getColWidthPx(key) }}>{estimate.objectName}</TableCell> :
                    key === 'value' ? <TableCell key={key} align="right" sx={{ whiteSpace: 'nowrap', width: getColWidthPx(key) }}>-</TableCell> :
                    key === 'status' ? <TableCell key={key} sx={{ whiteSpace: 'nowrap', width: getColWidthPx(key) }}>
                      <Chip
                        label={getStatusLabel(estimate.status)}
                        color={getStatusColor(estimate.status)}
                        size="small"
                      />
                    </TableCell> :
                    key === 'actions' ? <TableCell key={key} sx={{ whiteSpace: 'nowrap', width: getColWidthPx(key) }}>
                      <IconButton
                        id={`actions-${estimate.id}`}
                        size="small"
                        onClick={(e) => handleMenuClick(e, estimate)}
                      >
                        <MoreVert />
                      </IconButton>
                    </TableCell> :
                    null
                  ) : null)}
                </TableRow>
              );
            })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Menu akcji */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleEditEstimate}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edytuj</ListItemText>
        </MenuItem>
        <MenuItem onClick={()=>applyStatus('draft')}><ListItemText inset primary="Status: roboczy" /></MenuItem>
        <MenuItem onClick={()=>applyStatus('finished')}><ListItemText inset primary="Status: zakończony" /></MenuItem>
        <MenuItem onClick={()=>applyStatus('deleted')}><ListItemIcon><Delete fontSize="small" /></ListItemIcon><ListItemText primary="Usuń" /></MenuItem>
      </Menu>

      {/* Dialog do tworzenia nowego kosztorysu */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Nowy Kosztorys</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nazwa Kosztorysu"
                value={newEstimate.name}
                onChange={(e) => setNewEstimate({ ...newEstimate, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nazwa Obiektu"
                value={newEstimate.objectName}
                onChange={(e) => setNewEstimate({ ...newEstimate, objectName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Zamawiający"
                value={newEstimate.client}
                onChange={(e) => setNewEstimate({ ...newEstimate, client: e.target.value })}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                Narzuty Kosztorysu
              </Typography>
            </Grid>
            
            <Grid item xs={6} md={2.4}>
              <TextField
                fullWidth
                label="Kz (%)"
                type="number"
                value={newEstimate.kz}
                onChange={(e) => setNewEstimate({ ...newEstimate, kz: parseFloat(e.target.value) || 0 })}
                inputProps={{ step: 0.1, min: 0 }}
              />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <TextField
                fullWidth
                label="Kp (%)"
                type="number"
                value={newEstimate.kp}
                onChange={(e) => setNewEstimate({ ...newEstimate, kp: parseFloat(e.target.value) || 0 })}
                inputProps={{ step: 0.1, min: 0 }}
              />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <TextField
                fullWidth
                label="Z (%)"
                type="number"
                value={newEstimate.z}
                onChange={(e) => setNewEstimate({ ...newEstimate, z: parseFloat(e.target.value) || 0 })}
                inputProps={{ step: 0.1, min: 0 }}
              />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <TextField
                fullWidth
                label="Rg (zł/h)"
                type="number"
                value={newEstimate.rg}
                onChange={(e) => setNewEstimate({ ...newEstimate, rg: parseFloat(e.target.value) || 0 })}
                inputProps={{ step: 0.1, min: 0 }}
              />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <TextField
                fullWidth
                label="VAT (%)"
                type="number"
                value={newEstimate.vat}
                onChange={(e) => setNewEstimate({ ...newEstimate, vat: parseFloat(e.target.value) || 0 })}
                inputProps={{ step: 0.1, min: 0 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Anuluj</Button>
          <Button 
            onClick={handleCreateEstimate} 
            variant="contained"
            disabled={!newEstimate.name || !newEstimate.objectName || !newEstimate.client}
          >
            Utwórz
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)}>
        <DialogTitle>Wczytaj kosztorys</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Button variant="contained" component="label" startIcon={
                <Box
                  component="img"
                  src="/assets/icons/upload_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg"
                  alt="Upload"
                  sx={{ width: 20, height: 20 }}
                />
              }>
                Wybierz plik (.ath)
                <input 
                  type="file" 
                  hidden 
                  accept=".ath" 
                  onChange={(e)=> setImportFile(e.target.files?.[0] || null)} 
                />
              </Button>
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                {importFile ? importFile.name : 'Brak wybranego pliku'}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Anuluj</Button>
          <Button 
            onClick={async ()=>{
              if(!importFile) return;
              try{
                setImporting(true);
                const today = new Date().toISOString().split('T')[0];
                // Tworzymy nagłówek minimalny – wartości zostaną nadpisane przez importAth (z STRONA TYT oraz NARZUTY)
                const fallbackName = importFile.name.replace(/\.[^/.]+$/, '');
                const created = await createEstimate({
                  name: fallbackName,
                  object_name: '-',
                  client: '-',
                  markup_kp: 0,
                  markup_kz: 0,
                  markup_z: 0,
                  vat: 0,
                  preparer: 'SYSTEM',
                  prepared_at: today,
                });
                await importAth(created.id, importFile);
                console.log('Import ATH zakończony dla kosztorysu:', created.id);
                
                // Po imporcie dane kosztorysu są aktualizowane (nazwa, klient, narzuty z pliku ATH)
                // więc odświeżamy całą listę z serwera, aby mieć aktualne dane
                console.log('Odświeżanie listy kosztorysów po imporcie...');
                await loadEstimates();
                console.log('Lista kosztorysów odświeżona');
                
                setUploadDialogOpen(false);
                
                // Przejdź do widoku detali
                navigate(`/app/estimates/${created.id}`);
                setImportFile(null);
                setImportForm({
                  name: '',
                  objectName: '',
                  client: '',
                  kz: 5.0,
                  kp: 75.0,
                  z: 20.0,
                  rg: 45.0,
                  vat: 23.0,
                });
              }catch(err){
                console.error('Błąd importu ATH:', err);
                alert('Nie udało się wczytać kosztorysu z pliku ATH.');
              }finally{
                setImporting(false);
              }
            }}
            variant="contained"
            disabled={!importFile || importing}
          >
            {importing ? 'Wczytywanie…' : 'Wczytaj'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EstimatesList;
